import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { AddEditContactModal } from "./MyClients";
import {
  ChevronRight,
  Pencil,
  Mail,
  Phone,
  Wallet,
  TrendingUp,
  FileText,
  Scale,
  Loader2,
  ArrowRight,
} from "lucide-react";
import CaseStatCards from "../components/crm/cases/CaseStatCards";
import { StatusPill } from "../components/crm/cases/CasePresentation";

const TABS = [
  { key: "cases", label: "Cases" },
  { key: "expenses", label: "Expenses" },
  { key: "invoices", label: "Invoices" },
  { key: "documents", label: "Documents" },
  { key: "activity", label: "Activity" },
];

const formatINR = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtDateTime = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAddress = (a) => {
  if (!a) return "";
  const line1 = [a.building, a.street].filter(Boolean).join(", ");
  const line2 = [a.city, a.district, a.state].filter(Boolean).join(", ");
  const line3 = [a.pincode, a.country].filter(Boolean).join(", ");
  return [line1, line2, line3].filter(Boolean).join("\n");
};

function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { globalUser } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [activeTab, setActiveTab] = useState("cases");
  const [showEditModal, setShowEditModal] = useState(false);

  const handleSaveClient = async (formData) => {
    try {
      await updateDoc(doc(db, "clients", client.id), {
        ...formData,
        updatedAt: serverTimestamp(),
      });
      toast.success("Client updated successfully");
      setShowEditModal(false);
      const snap = await getDoc(doc(db, "clients", client.id));
      if (snap.exists()) setClient({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("Error updating client:", e);
      toast.error("Failed to update client");
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id || !globalUser?.uid) return;
      setLoading(true);
      setNotFound(false);
      try {
        const snap = await getDoc(doc(db, "clients", id));
        if (!snap.exists()) {
          setNotFound(true);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        // Owner-scoped: a lawyer only sees their own contacts.
        if (data.owner && data.owner !== globalUser.uid) {
          setNotFound(true);
          return;
        }
        setClient(data);

        // Invoices raised for this contact.
        if (localStorage.getItem("userType") !== "client") {
          try {
            const invSnap = await getDocs(
              query(
                collection(db, "lawyers", globalUser.uid, "invoices"),
                where("contactId", "==", id)
              )
            );
            setInvoices(invSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
          } catch (e) {
            console.error("Error loading invoices:", e);
          }
        }

        // Cases shared with this client (registered portal clients only).
        if (data.portalClientUid) {
          try {
            const [pend, disp] = await Promise.all([
              getDocs(
                query(
                  collection(db, "pending"),
                  where("team", "array-contains", data.portalClientUid)
                )
              ),
              getDocs(
                query(
                  collection(db, "disposed"),
                  where("team", "array-contains", data.portalClientUid)
                )
              ),
            ]);
            const seen = new Set();
            const list = [];
            [...pend.docs, ...disp.docs].forEach((d) => {
              if (seen.has(d.id)) return;
              seen.add(d.id);
              const cd = d.data();
              list.push({
                id: d.id,
                caseNo: cd.caseNo,
                caseType: cd.caseType,
                petitionerName: cd.petitionerName,
                respondentName: cd.respondentName,
                court: cd.courtName,
                status: cd.status,
                nextHearingDate: cd.nextHearingDate,
                isShared: true,
              });
            });
            setCases(list);
          } catch (e) {
            console.error("Error loading cases:", e);
          }
        }
      } catch (e) {
        console.error("Error loading client:", e);
        toast.error("Failed to load client");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, globalUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex h-[60vh] items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (notFound || !client) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] px-4 py-10 text-center text-slate-500">
        <p className="text-lg">Client not found.</p>
        <Link to="/portal/myclients" className="mt-3 inline-block text-sm font-medium text-blue-600 hover:underline">
          ← Back to Clients
        </Link>
      </div>
    );
  }

  const fullName = `${client.salutation ? client.salutation + " " : ""}${
    client.name || ""
  }`.trim();
  const initial = (client.name || "?").trim().charAt(0).toUpperCase();
  const totalBilled = invoices.reduce((s, i) => s + Number(i.totalAmount || 0), 0);
  const pendingDue = invoices.reduce((s, i) => s + Number(i.dueAmount || 0), 0);
  const isActive =
    Boolean(client.portalClientUid) ||
    pendingDue > 0 ||
    totalBilled > 0 ||
    cases.length > 0;
  const address = formatAddress(client.primaryAddress);
  const customFields = Array.isArray(client.customFields)
    ? client.customFields.filter((f) => f && (f.key || f.value))
    : [];

  const InfoBlock = ({ label, children }) => (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <div className="mt-1 text-sm text-slate-700">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header: breadcrumb + actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1.5 text-sm">
            <Link
              to="/portal/myclients"
              className="text-slate-500 transition-colors hover:text-slate-800"
            >
              Clients
            </Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">
              {fullName || "Client"}
            </span>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="inline-flex items-center gap-2 self-start rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:self-auto"
          >
            <Pencil className="h-4 w-4" /> Edit Client
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* ===== Left: profile card ===== */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-1">
            <div className="flex items-start gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-xl font-bold text-yellow-700">
                {initial}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-bold text-slate-900">
                    {fullName || "Unnamed"}
                  </h1>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                      isActive
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-slate-100 text-slate-600 ring-slate-200"
                    }`}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-slate-500">
                  {client.company || "Individual"}
                </p>
                {client.email && (
                  <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <Mail size={14} className="shrink-0 text-slate-400" />
                    <span className="truncate">{client.email}</span>
                  </p>
                )}
                {client.contact && (
                  <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                    <Phone size={14} className="shrink-0 text-slate-400" />
                    {client.contact}
                    {client.contactAlternate ? `, ${client.contactAlternate}` : ""}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 space-y-5 border-t border-slate-100 pt-5">
              {client.gstin && (
                <InfoBlock label="GSTIN">{client.gstin}</InfoBlock>
              )}
              {client.group && (
                <InfoBlock label="Group">{client.group}</InfoBlock>
              )}
              {address && (
                <InfoBlock label="Billing Address">
                  <span className="whitespace-pre-line">{address}</span>
                </InfoBlock>
              )}
              {customFields.map((f, i) => (
                <InfoBlock key={i} label={f.key || "Field"}>
                  {f.value || "—"}
                </InfoBlock>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-5">
              <InfoBlock label="Client Since">
                {fmtDate(client.createdAt)}
              </InfoBlock>
              <InfoBlock label="Last Updated">
                {fmtDateTime(client.updatedAt || client.createdAt)}
              </InfoBlock>
            </div>
          </div>

          {/* ===== Right: stats + tabs ===== */}
          <div className="space-y-4 lg:col-span-2">
            <CaseStatCards
              cards={[
                { label: "Total Billed", value: formatINR(totalBilled), Icon: TrendingUp, tint: "blue" },
                { label: "Pending Dues", value: formatINR(pendingDue), Icon: Wallet, tint: "amber" },
                { label: "Total Invoices", value: invoices.length, Icon: FileText, tint: "purple" },
                { label: "Total Cases", value: cases.length, Icon: Scale, tint: "green" },
              ]}
            />

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              {/* Tabs */}
              <div className="flex flex-wrap gap-1 border-b border-slate-200 px-3">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className={`relative px-3 py-3 text-sm font-medium transition-colors ${
                      activeTab === t.key
                        ? "text-slate-900"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {t.label}
                    {activeTab === t.key && (
                      <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-yellow-500" />
                    )}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {activeTab === "cases" &&
                  (cases.length === 0 ? (
                    <EmptyTab
                      icon={<Scale className="h-7 w-7" />}
                      text={
                        client.portalClientUid
                          ? "No cases shared with this client yet."
                          : "Cases link only to registered portal clients."
                      }
                    />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] text-left text-sm">
                        <thead>
                          <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            <th className="px-2 py-2">Case Title</th>
                            <th className="px-2 py-2">Case No.</th>
                            <th className="px-2 py-2">Court</th>
                            <th className="px-2 py-2">Status</th>
                            <th className="px-2 py-2">Next Hearing</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {cases.map((c) => (
                            <tr
                              key={c.id}
                              onClick={() =>
                                navigate(`/portal/case/${c.id}?from=shared`)
                              }
                              className="cursor-pointer transition-colors hover:bg-slate-50"
                            >
                              <td className="px-2 py-3">
                                <div className="max-w-[220px] truncate font-medium text-slate-800">
                                  {[c.petitionerName, c.respondentName]
                                    .filter(Boolean)
                                    .join(" vs ") || c.caseType || "—"}
                                </div>
                              </td>
                              <td className="px-2 py-3 text-slate-600">
                                {c.caseNo || "—"}
                              </td>
                              <td className="px-2 py-3">
                                <div className="max-w-[160px] truncate text-slate-600">
                                  {c.court || "—"}
                                </div>
                              </td>
                              <td className="px-2 py-3">
                                <StatusPill status={c.status} />
                              </td>
                              <td className="px-2 py-3 text-slate-600">
                                {c.nextHearingDate || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <Link
                        to="/portal/allcases"
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
                      >
                        View all cases <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  ))}

                {activeTab === "invoices" &&
                  (invoices.length === 0 ? (
                    <EmptyTab
                      icon={<FileText className="h-7 w-7" />}
                      text="No invoices raised for this client yet."
                    />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[560px] text-left text-sm">
                        <thead>
                          <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            <th className="px-2 py-2">Invoice No.</th>
                            <th className="px-2 py-2">Amount</th>
                            <th className="px-2 py-2">Due</th>
                            <th className="px-2 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {invoices.map((inv) => (
                            <tr key={inv.id} className="hover:bg-slate-50">
                              <td className="px-2 py-3 font-medium text-slate-800">
                                {inv.invoiceNumber || inv.id.slice(0, 6)}
                              </td>
                              <td className="px-2 py-3 text-slate-700">
                                {formatINR(inv.totalAmount)}
                              </td>
                              <td className="px-2 py-3 text-slate-700">
                                {Number(inv.dueAmount || 0) > 0
                                  ? formatINR(inv.dueAmount)
                                  : "—"}
                              </td>
                              <td className="px-2 py-3">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                    inv.status === "paid"
                                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                      : "bg-amber-50 text-amber-700 ring-amber-200"
                                  }`}
                                >
                                  {inv.status === "paid" ? "Paid" : "Raised"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}

                {activeTab === "expenses" && (
                  <EmptyTab
                    icon={<Wallet className="h-7 w-7" />}
                    text="Client-linked expenses view is coming soon."
                  />
                )}
                {activeTab === "documents" && (
                  <EmptyTab
                    icon={<FileText className="h-7 w-7" />}
                    text="Documents view is coming soon."
                  />
                )}
                {activeTab === "activity" && (
                  <EmptyTab
                    icon={<Loader2 className="h-7 w-7" />}
                    text="Activity timeline is coming soon."
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <AddEditContactModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        contact={client}
        onSave={handleSaveClient}
      />
    </div>
  );
}

function EmptyTab({ icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
      <div className="mb-3 opacity-50">{icon}</div>
      <p className="text-sm">{text}</p>
    </div>
  );
}

export default ClientDetails;
